import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useTimer from '../../../shared/hooks/useTimer';
import ExamTimer from './ExamTimer';
import QuestionNav from './QuestionNav';
import SingleChoice from './SingleChoice';
import MultiChoice from './MultiChoice';
import DragDrop from './DragDrop';
import ScenarioTestlet from './ScenarioTestlet';
import ConfirmDialog from '../../../shared/components/ConfirmDialog';
import DomainBadge from '../../../shared/components/DomainBadge';
import { renderMarkdown } from '../../../shared/utils/markdownLite';

/**
 * Active exam container. Manages answers, flags, timer, and navigation.
 * CRITICAL: Never shows correct answers during the exam.
 */
export default function ExamRunner({ exam, questions, onSubmit }) {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flags, setFlags] = useState(new Set());
  const [visitedSet, setVisitedSet] = useState(new Set([0]));
  const [showNavPanel, setShowNavPanel] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [confirmQuit, setConfirmQuit] = useState(false);
  const questionTimeRef = useRef({});
  const lastTimeRef = useRef(Date.now());

  const timer = useTimer({
    initialSeconds: (exam.time_limit_minutes || 120) * 60,
    mode: 'countdown',
    onExpire: () => handleAutoSubmit(),
    warningThresholds: [1800, 600, 300],
  });

  // Start timer on mount
  useEffect(() => {
    timer.start();
    return () => timer.pause();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track time per question
  useEffect(() => {
    const now = Date.now();
    const prevQId = questions[currentIndex - 1]?.id;
    if (prevQId) {
      const elapsed = (now - lastTimeRef.current) / 1000;
      questionTimeRef.current[prevQId] = (questionTimeRef.current[prevQId] || 0) + elapsed;
    }
    lastTimeRef.current = now;
  }, [currentIndex, questions]);

  const currentQ = questions[currentIndex];

  const handleAnswer = useCallback((answer) => {
    if (!currentQ) return;
    setAnswers(prev => ({ ...prev, [currentQ.id]: answer }));
  }, [currentQ]);

  const handleFlag = useCallback(() => {
    if (!currentQ) return;
    setFlags(prev => {
      const next = new Set(prev);
      if (next.has(currentQ.id)) next.delete(currentQ.id);
      else next.add(currentQ.id);
      return next;
    });
  }, [currentQ]);

  const goTo = useCallback((idx) => {
    if (idx >= 0 && idx < questions.length) {
      setCurrentIndex(idx);
      setVisitedSet(prev => new Set([...prev, idx]));
    }
  }, [questions.length]);

  const handleSubmit = useCallback(() => {
    timer.pause();
    // Record final question time
    const qId = questions[currentIndex]?.id;
    if (qId) {
      const elapsed = (Date.now() - lastTimeRef.current) / 1000;
      questionTimeRef.current[qId] = (questionTimeRef.current[qId] || 0) + elapsed;
    }

    onSubmit({
      answers,
      flags: Array.from(flags),
      timePerQuestion: questionTimeRef.current,
      completed_at: new Date().toISOString(),
    });
  }, [timer, answers, flags, onSubmit, questions, currentIndex]);

  const handleAutoSubmit = useCallback(() => {
    handleSubmit();
  }, [handleSubmit]);

  const unansweredCount = questions.filter(q => answers[q.id] === undefined).length;
  const questionIds = questions.map(q => q.id);

  if (!currentQ) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-2">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600 font-medium">
              Question {currentIndex + 1} of {questions.length}
            </span>
            <div className="w-32 bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-blue-500 h-1.5 rounded-full transition-all"
                style={{ width: `${((Object.keys(answers).length) / questions.length) * 100}%` }}
              />
            </div>
          </div>
          <ExamTimer seconds={timer.seconds} isRunning={timer.isRunning} />
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowNavPanel(!showNavPanel)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Nav
            </button>
            <button
              onClick={() => setConfirmQuit(true)}
              className="px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
            >
              Quit
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 flex gap-4">
        {/* Main question area */}
        <div className="flex-1 min-w-0">
          {/* Question prompt */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <DomainBadge domainId={currentQ.domain} size="xs" />
              {flags.has(currentQ.id) && (
                <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">Flagged</span>
              )}
            </div>

            {/* Diagram */}
            {currentQ.diagram && (
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg mb-4 text-sm font-mono overflow-x-auto">
                {currentQ.diagram}
              </pre>
            )}

            <div className="text-gray-900 leading-relaxed text-base">
              {renderMarkdown(currentQ.prompt)}
            </div>
          </div>

          {/* Answer area */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
            {currentQ.type === 'single_choice' && (
              <SingleChoice question={currentQ} answer={answers[currentQ.id]} onChange={handleAnswer} />
            )}
            {currentQ.type === 'multi_choice' && (
              <MultiChoice question={currentQ} answer={answers[currentQ.id] || []} onChange={handleAnswer} />
            )}
            {currentQ.type === 'drag_drop' && (
              <DragDrop question={currentQ} answer={answers[currentQ.id] || {}} onChange={handleAnswer} />
            )}
            {currentQ.type === 'scenario' && (
              <ScenarioTestlet question={currentQ} answer={answers[currentQ.id] || {}} onChange={handleAnswer} />
            )}
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => goTo(currentIndex - 1)}
              disabled={currentIndex === 0}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-30"
            >
              ← Previous
            </button>

            <button
              onClick={handleFlag}
              className={`px-4 py-2 rounded-lg border text-sm ${
                flags.has(currentQ.id)
                  ? 'bg-orange-50 border-orange-300 text-orange-600'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {flags.has(currentQ.id) ? '🚩 Flagged' : '🏳️ Flag for Review'}
            </button>

            {currentIndex < questions.length - 1 ? (
              <button
                onClick={() => goTo(currentIndex + 1)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Next →
              </button>
            ) : (
              <button
                onClick={() => setConfirmSubmit(true)}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                Submit Exam
              </button>
            )}
          </div>
        </div>

        {/* Side nav panel */}
        {showNavPanel && (
          <div className="w-64 shrink-0 hidden md:block">
            <QuestionNav
              totalQuestions={questions.length}
              currentIndex={currentIndex}
              answers={answers}
              flags={flags}
              visitedSet={visitedSet}
              questionIds={questionIds}
              onNavigate={goTo}
            />
          </div>
        )}
      </div>

      {/* Submit confirmation */}
      <ConfirmDialog
        isOpen={confirmSubmit}
        title="Submit Exam?"
        message={unansweredCount > 0
          ? `You have ${unansweredCount} unanswered question${unansweredCount > 1 ? 's' : ''}. Submit anyway?`
          : 'Are you sure you want to submit your exam?'
        }
        confirmText="Submit"
        onConfirm={handleSubmit}
        onCancel={() => setConfirmSubmit(false)}
      />

      {/* Quit confirmation */}
      <ConfirmDialog
        isOpen={confirmQuit}
        title="Quit Exam?"
        message="Your progress will be lost. Are you sure?"
        confirmText="Quit"
        variant="danger"
        onConfirm={() => navigate('/exam')}
        onCancel={() => setConfirmQuit(false)}
      />
    </div>
  );
}
